import { hasOwn } from './domain.js';
import { closeMonthOf, countsInAggregates, countsInHeadcount, fxSalary, hiringCostOf, loadedCost, retentionCostOf, severanceCostOf } from './cost.js';
import { currentBucket, strandedEligible, strandedPlanOf, waveOf } from './decisions.js';
export const HEADCOUNT = () => 1;
export const FTE = (p) => Number(p.fte) || 0;
/**
 * RAW annual salary — the app's "Cost" metric, and the one every cost bridge in it uses.
 *
 * Named and exported because its absence was a trap. Core exported exactly one cost weight,
 * `costMetric`, which is LOADED cost — and the app's three-way metric toggle is raw salary
 * (orgchart.html:7681), used at all nine of its cost-basis bridge call sites. So the only exported
 * cost metric was the wrong one for every bridge, and reaching for it was the obvious thing to do.
 * Two shipped panels retyped the lambda by hand to avoid it, one with a comment naming the trap;
 * the deck did not, and printed loaded cost under a footnote saying raw salary.
 *
 * A FACTORY now, where it used to be a const: raw means unloaded, not unconverted (cost.ts states
 * the rule), so the weight needs the fx grid. `null` is the explicit way to say "stored values,
 * no conversion" — it exists so a caller without assumptions in reach states that choice instead
 * of inheriting it, which is how one pane converting and its neighbour not would start.
 */
export function salaryMetric(assumptions) {
    if (assumptions === null)
        return (p) => Number(p.salary) || 0;
    return (p) => fxSalary(p, assumptions);
}
/**
 * LOADED cost — salary × the loaded-cost multiplier.
 *
 * This is the economics weight, not the bridge weight: TSA fees, stranded run-rate and severance are
 * priced loaded, and the deck's own footnote states the distinction ("Org costs = raw salaries; TSA /
 * stranded economics = loaded cost"). Reach for `salaryMetric` for anything captioned as a cost bridge.
 */
export function costMetric(assumptions) {
    return (p) => loadedCost(p, assumptions);
}
/**
 * The three weights the metric toggle offers, so a surface cannot assemble the set wrongly.
 *
 * Every pane that offers Headcount / FTE / Cost picks from here rather than building its own map,
 * which is what let two of them disagree about what "Cost" means.
 */
export function metricFn(key, assumptions) {
    return key === 'fte' ? FTE : key === 'cost' ? salaryMetric(assumptions) : HEADCOUNT;
}
/**
 * Cross-stage carve-out bridge, computed from decision state alone — independent of any view lens.
 *
 * Identities the result satisfies by construction:
 *   day1 = perimeter + transfers + d1BuildUps
 *   day2 = day1 − rightsizing + tsaRepl + d2BuildUps
 *   asis = remainOrg + nlr + perimeter + transfers      (build-ups do not exist as-is)
 *
 * Pass `design` to price a scenario other than the live one.
 */
export function computeStageBridge(state, options = {}) {
    const fn = options.fn ?? HEADCOUNT;
    const design = options.design ?? state.design;
    const bucketOf = (id) => (hasOwn(design.buckets, id) ? design.buckets[id] : 'Remain');
    const metaOf = (id) => hasOwn(design.bucketMeta, id)
        ? design.bucketMeta[id]
        : { src: 'manual', joinStage: 'perimeter' };
    const B = {
        asis: 0, remainOrg: 0, nlr: 0, perimeter: 0, transfers: 0, d1BuildUps: 0, day1: 0,
        rightsizing: 0, tsaRepl: 0, d2BuildUps: 0, day2: 0, remainRightsize: 0,
    };
    for (const person of state.employees) {
        if (!countsInAggregates(person))
            continue;
        const w = Number(fn(person)) || 0;
        B.asis += w;
        const bucket = bucketOf(person.id);
        if (bucket === 'No longer required') {
            B.nlr += w;
            continue;
        }
        if (bucket !== 'Carve-out') {
            B.remainOrg += w;
            // A Remain-side rightsizing leaves RemainCo after Day 1 — the remain mirror's Day 2 movement.
            // Redeploys stay employed, so they have no bridge effect.
            const plan = design.strandedPlan[person.id];
            if (plan && plan.action === 'rightsize' && person.dedication != null && person.dedication > 0) {
                B.remainRightsize += w;
            }
            continue;
        }
        const meta = metaOf(person.id);
        if (meta.joinStage === 'day1')
            B.transfers += w;
        else
            B.perimeter += w;
        B.day1 += w;
        if (meta.day2Exit)
            B.rightsizing += w;
        else
            B.day2 += w;
    }
    for (const bu of design.buildUps) {
        if (!countsInHeadcount(bu) || bucketOf(bu.id) !== 'Carve-out')
            continue;
        const w = Number(fn(bu)) || 0;
        if (waveOf(bu) === 'day1') {
            B.d1BuildUps += w;
            B.day1 += w;
            if (bu.day2Exit)
                B.rightsizing += w;
            else
                B.day2 += w;
        }
        else {
            if (bu.tsaId)
                B.tsaRepl += w;
            else
                B.d2BuildUps += w;
            B.day2 += w;
        }
    }
    return B;
}
/**
 * The summary's six organisation sizes, from three already-computed bridges.
 *
 * A function rather than six expressions in a template, which is what the app has
 * (orgchart.html:7961-7970). Five of the rows are a field read; the sixth is a SUBTRACTION —
 * `remainOrg − remainRightsize` — and an arithmetic step written inside markup is a step no harness can
 * hold. That row is the RemainCo steady state a steering committee reads off the page.
 *
 * Takes the bridges rather than the state so the caller computes each weight once: the pane needs all
 * three for its flow tiles too, and a second pass over a 200,000-row roster to restate the same figure
 * is a real cost.
 */
export function summarySizeRows(bridges) {
    const { hc, fte, cost } = bridges;
    const row = (key, label, pick, opts = {}) => ({
        key,
        label,
        existingOnly: opts.existingOnly ?? false,
        hc: pick(hc),
        fte: pick(fte),
        cost: pick(cost),
        strong: opts.strong ?? false,
    });
    return [
        row('asis', 'As-is (whole organisation)', (B) => B.asis),
        row('perimeter', 'Carve-out — Perimeter', (B) => B.perimeter),
        row('day1', 'Carve-out — Day 1', (B) => B.day1, { strong: true }),
        row('day2', 'Carve-out — Day 2 (stand-alone)', (B) => B.day2, { strong: true }),
        row('remain-org', 'RemainCo after Day 1', (B) => B.remainOrg, { existingOnly: true }),
        row('remain-steady', 'RemainCo steady state', (B) => B.remainOrg - B.remainRightsize, {
            existingOnly: true,
            strong: true,
        }),
    ];
}
/**
 * Severance for every population that leaves an organisation as part of the design — No-longer-
 * required at close, carve-side Day 2 exits, and RemainCo stranded rightsizings — plus the arrival
 * side, where every planned position carries a one-off hiring cost.
 *
 * Day 2 exits count employees only: a cancelled build-up position has no incumbent to sever.
 */
export function computeOneOffCosts(state, options = {}) {
    const design = options.design ?? state.design;
    const { assumptions } = state;
    const O = {
        nlrSeverance: 0, nlrN: 0, d2Severance: 0, d2N: 0, strandedSeverance: 0, strandedN: 0,
        carveHiring: 0, carveBuN: 0, remainHiring: 0, remainBuN: 0,
        hiring: 0, severance: 0, retention: 0, retentionN: 0, total: 0, n: 0,
    };
    const bucketOf = (id) => (hasOwn(design.buckets, id) ? design.buckets[id] : 'Remain');
    // No budget, no line: with retentionPct unset the count stays 0 too, so pre-retention
    // projects are bit-identical rather than sprouting a zero-cost row with a headcount in it.
    const retentionOn = (Number(assumptions.retentionPct) || 0) > 0;
    for (const person of state.employees) {
        if (!countsInAggregates(person))
            continue;
        const bucket = bucketOf(person.id);
        if (bucket === 'No longer required') {
            O.nlrSeverance += severanceCostOf(person, assumptions);
            O.nlrN++;
            continue;
        }
        if (bucket === 'Carve-out') {
            if ((design.bucketMeta[person.id] ?? {}).day2Exit) {
                O.d2Severance += severanceCostOf(person, assumptions);
                O.d2N++;
                if (retentionOn) {
                    O.retention += retentionCostOf(person, assumptions);
                    O.retentionN++;
                }
            }
            continue;
        }
        const plan = design.strandedPlan[person.id];
        if (plan && plan.action === 'rightsize' && person.dedication != null && person.dedication > 0) {
            O.strandedSeverance += severanceCostOf(person, assumptions);
            O.strandedN++;
            if (retentionOn) {
                O.retention += retentionCostOf(person, assumptions);
                O.retentionN++;
            }
        }
    }
    for (const bu of design.buildUps) {
        if (!countsInHeadcount(bu))
            continue;
        if (bucketOf(bu.id) === 'Carve-out') {
            O.carveHiring += hiringCostOf(bu, assumptions);
            O.carveBuN++;
        }
        else {
            O.remainHiring += hiringCostOf(bu, assumptions);
            O.remainBuN++;
        }
    }
    O.hiring = O.carveHiring + O.remainHiring;
    O.severance = O.nlrSeverance + O.d2Severance + O.strandedSeverance;
    // `retentionN` deliberately stays OUT of `n`: the retained people ARE the d2/stranded people
    // already counted once, and a person is not two people because two cheques reach them.
    O.total = O.severance + O.hiring + O.retention;
    O.n = O.nlrN + O.d2N + O.strandedN + O.carveBuN + O.remainBuN;
    return O;
}
/* ------------------------------------------------------------------ *
 * TSA economics
 * ------------------------------------------------------------------ */
/** The side that must BUILD a TSA's replacement: whoever loses the service when it ends. */
export function tsaReplacementSide(tsa) {
    return tsa && tsa.direction === 'provided' ? 'Remain' : 'Carve-out';
}
export function tsaById(design, id) {
    return design.tsas.find((t) => t.id === id);
}
/** Build-ups hired to replace a TSA — counted only on that TSA's correct side. */
export function tsaReplacements(design, tsaId) {
    const side = tsaReplacementSide(tsaById(design, tsaId));
    return design.buildUps.filter((b) => b.tsaId === tsaId && currentBucket(design, b.id) === side);
}
/** Build-ups whose TSA link sits on the wrong side — surfaced by diagnostics, counted nowhere. */
export function tsaWrongSideLinks(design) {
    return design.buildUps.filter((b) => {
        if (!b.tsaId)
            return false;
        const tsa = tsaById(design, b.tsaId);
        if (!tsa)
            return false;
        return currentBucket(design, b.id) !== tsaReplacementSide(tsa);
    });
}
/**
 * The longest month any surface may need to draw.
 *
 * The original clamped every horizon to 36, which is the single most common TSA term there is — so at
 * exactly that term the `+ 3` tail that makes the last bar the post-transition steady state was cut
 * off, and the chart showed the transition still running at its right edge. Worse, `tsaIncomeMonths`
 * was never clamped, so a tile could name a month past the end of the axis beside it. The bound is the
 * schema's own duration ceiling, so the drawn range can never end before the last event.
 */
const HORIZON_CEILING = 120;
/*
  A FOLD RATHER THAN A SPREAD, and the difference is a crash rather than a slowdown.

  `Math.max(0, ...months)` passes one argument per element, and V8 refuses somewhere around 125,000
  of them with a `RangeError: Maximum call stack size exceeded`. Two of the three callers build this
  array from the roster — `computeStrandedTimeline` contributes two entries per carrier — so above
  roughly 63,000 people with a dedication, `priceScenario` throws outright. Not slowly: the
  comparison pane, the deck's compare slide and the perimeter sensitivity sweep all stop working at
  once, and the error names a stack rather than a roster. Measured before the fix: 62,376 carriers
  priced in 42 ms, 65,000 threw.

  The loop is also faster than the spread it replaces, which is the usual way round for this pair.
*/
function horizonOver(months) {
    let last = 0;
    for (const m of months)
        if (m > last)
            last = m;
    return Math.max(6, Math.min(HORIZON_CEILING, last + 3));
}
/**
 * How many months an agreement actually bills, on the "months after Day 1" clock every chart uses.
 *
 * The term, unless it was ended early — and an exit with NO month recorded bills nothing, which is both
 * the behaviour before this field existed and what the frozen app's data can express. So a project
 * imported from the app is unchanged by the field's arrival, and only a month somebody typed here
 * changes a figure.
 */
export function billedMonths(tsa) {
    const dur = Math.max(0, Number(tsa.durationMonths) || 0);
    if (tsa.status !== 'exited')
        return dur;
    const exited = tsa.exitedAtMonth;
    return exited == null ? 0 : Math.max(0, Math.min(dur, exited));
}
export function computeTsaEconomics(state, options = {}) {
    const design = options.design ?? state.design;
    const { assumptions } = state;
    const mult = Number(assumptions.loadedCostMultiplier) || 1;
    const perTsa = design.tsas.map((tsa) => {
        const fee = Number(tsa.monthlyCost) || 0;
        const sign = tsa.direction === 'provided' ? -1 : 1;
        const dur = Number(tsa.durationMonths) || 0;
        const termAgreed = dur > 0;
        const repl = tsaReplacements(design, tsa.id);
        const replFilled = repl.filter(countsInHeadcount);
        const replUndatedN = replFilled.filter((b) => b.neededBy == null).length;
        const replMonthly = replFilled.reduce((a, b) => a + loadedCost(b, assumptions), 0) / 12;
        const readyMonth = replFilled.length && replUndatedN === 0
            ? Math.max(...replFilled.map((b) => Number(b.neededBy)))
            : null;
        const exitGap = readyMonth != null && termAgreed ? readyMonth - dur : null;
        return {
            tsa,
            fee,
            sign,
            dur,
            repl,
            replFilled,
            replUndatedN,
            replMonthly,
            readyMonth,
            termAgreed,
            // An agreement recorded as over bills nothing forward. Every one of these charts is "months after
            // Day 1", and `exited` was already honoured by the timeline's greying and by the uncovered rule —
            // it was the money that ignored it, so marking a TSA over, the one action that should raise
            // RemainCo's exposure, changed no figure anywhere.
            cumFees: fee * billedMonths(tsa) * sign,
            // Priced from the filled subset, so an unfilled requisition yields no delta rather than a saving
            // equal to the whole fee.
            monthlyDelta: replFilled.length ? replMonthly - fee : null,
            exitGap,
            covered: replFilled.length > 0 && replUndatedN === 0 && (!termAgreed || exitGap <= 0),
        };
    });
    const feesPaid = perTsa.filter((x) => x.sign > 0).reduce((a, x) => a + x.cumFees, 0);
    const feesReceived = perTsa.filter((x) => x.sign < 0).reduce((a, x) => a - x.cumFees, 0);
    // The carve-out's transition curve counts only ITS replacements. Remain-side hires replacing
    // provided (reverse) TSAs are RemainCo's internalisation cost, reported separately; a wrong-side
    // legacy link counts nowhere and is flagged by diagnostics instead.
    const linkedRepl = design.buildUps.filter((b) => countsInHeadcount(b) &&
        b.tsaId &&
        currentBucket(design, b.id) === 'Carve-out' &&
        tsaReplacementSide(tsaById(design, b.tsaId)) === 'Carve-out');
    const remainRepl = design.buildUps.filter((b) => countsInHeadcount(b) &&
        b.tsaId &&
        currentBucket(design, b.id) === 'Remain' &&
        tsaReplacementSide(tsaById(design, b.tsaId)) === 'Remain');
    /*
      This horizon is the transition chart's, and it is deliberately NOT the exit timeline's.
  
      Three surfaces in the original all captioned their axis "months after Day 1" and computed it from
      three different input sets — and a comment above the third asserted it was the same formula as this
      one. It is not: this one takes every TSA duration plus the needed-by of correct-side CARVE-OUT
      replacements; `tsaTimeline` takes every duration plus every marker it actually draws; the stranded
      timeline takes received-direction durations plus plan months. Unifying them would silently move
      every bar on one of the three charts, so each has its own named function and its own tests.
    */
    const horizon = horizonOver([
        ...perTsa.map((x) => x.dur),
        ...linkedRepl.map((b) => Number(b.neededBy) || 0),
    ]);
    // Net TSA fees still running, plus the loaded run-rate of replacements already in place: fees fade
    // out as each TSA ends, replacement cost ramps in from its needed-by month.
    const monthly = [];
    for (let m = 1; m <= horizon; m++) {
        let feesPaidM = 0;
        let feesReceivedM = 0;
        for (const x of perTsa) {
            // The same rule as the cumulative total, so a tile and the curve beneath it cannot disagree.
            if (m > billedMonths(x.tsa))
                continue;
            if (x.sign > 0)
                feesPaidM += x.fee;
            else
                feesReceivedM += x.fee;
        }
        let repl = 0;
        for (const b of linkedRepl) {
            // An undated hire is omitted, not ramped in at month 1. Pinning it to 1 charged a full loaded
            // run-rate from Day 1 for a replacement nobody had scheduled, alongside the fee it was meant to
            // replace — so the curve double-counted the service for the whole life of the agreement.
            if (b.neededBy == null)
                continue;
            if (m >= Math.max(1, b.neededBy))
                repl += loadedCost(b, assumptions) / 12;
        }
        const fees = feesPaidM - feesReceivedM;
        monthly.push({
            m,
            fees,
            feesPaid: feesPaidM,
            feesReceived: feesReceivedM,
            repl,
            total: fees + repl,
        });
    }
    const uncovered = perTsa.filter((x) => x.tsa.status !== 'exited' && !x.covered).length;
    return {
        perTsa,
        feesPaid,
        feesReceived,
        feesNet: feesPaid - feesReceived,
        netMonthAtDay1: monthly.length ? monthly[0].fees : 0,
        replRunRate: linkedRepl.reduce((a, b) => a + loadedCost(b, assumptions), 0),
        remainInternalization: remainRepl.reduce((a, b) => a + loadedCost(b, assumptions), 0),
        remainReplN: remainRepl.length,
        horizon,
        monthly,
        replUndatedN: linkedRepl.filter((b) => b.neededBy == null).length,
        uncovered,
        mult,
    };
}
/**
 * The register's exit timeline: one row per agreement, with a marker per replacement.
 *
 * The row model is here rather than in the panel for the reason the survey gave: the panes the original
 * built inline are the ones whose defects survived, because a compute function is what the parity
 * harness can hold. The panel places the marks; every month and every flag on them is decided here.
 *
 * Its horizon is its own. The original derived it from every build-up in the project — including Remain
 * hires and unlinked Day-2 hires that this chart never draws — so one unrelated hire dated M30 stretched
 * the axis to M33 and compressed every TSA bar with nothing on screen saying why. Here it spans exactly
 * what it draws.
 */
export function tsaTimeline(state, options = {}) {
    const design = options.design ?? state.design;
    const rows = design.tsas.map((tsa) => {
        const dur = Number(tsa.durationMonths) || 0;
        const termAgreed = dur > 0;
        const markers = tsaReplacements(design, tsa.id).map((b) => {
            const month = b.neededBy == null ? null : Number(b.neededBy);
            return {
                buildUpId: b.id,
                name: b.name,
                month,
                late: month != null && termAgreed && month > dur,
                filled: countsInHeadcount(b),
            };
        });
        const usable = markers.filter((m) => m.filled && m.month != null);
        return {
            tsa,
            dur,
            termAgreed,
            /*
              An exited agreement is never flagged, matching the original — the bar is greyed instead.
      
              Otherwise: no usable marker at all, or every usable one lands late. The original tested only
              `!repl.length` against the UNFILLED list, so an open requisition suppressed the red bar; and it
              gave the worse condition the weaker signal, keeping the bar its direction colour and ambering
              only the diamond when a replacement was going to miss the deadline.
            */
            uncovered: tsa.status !== 'exited' && (usable.length === 0 || usable.every((m) => m.late)),
            // Only where it says something the term does not: an exit with a month on it. See the field.
            billedTo: tsa.status === 'exited' && tsa.exitedAtMonth != null ? billedMonths(tsa) : null,
            markers,
            undatedN: markers.filter((m) => m.month == null).length,
            wrongSideN: design.buildUps.filter((b) => b.tsaId === tsa.id && currentBucket(design, b.id) !== tsaReplacementSide(tsa)).length,
        };
    });
    const horizon = horizonOver([
        ...rows.map((r) => r.dur),
        ...rows.flatMap((r) => r.markers.map((m) => m.month ?? 0)),
    ]);
    return { rows, horizon, scaleStep: horizon > 18 ? 6 : 3 };
}
/**
 * The RemainCo stranded-cost lifecycle: gross stranded run-rate at Day 1, temporarily offset by
 * received-TSA fee income (which stops as each agreement ends, so exposure steps up), mitigated by
 * the per-person plan, leaving the residual steady-state cost as the headline.
 *
 * Month semantics, ported exactly: an action is effective FROM month M — the carrier stops counting
 * at m ≥ max(M, 1), and severance is booked in month max(M, 1).
 */
export function computeStrandedTimeline(state, options = {}) {
    const design = options.design ?? state.design;
    const { assumptions } = state;
    const carriers = state.employees
        .filter((p) => countsInHeadcount(p) && strandedEligible(design, p))
        .map((person) => ({
        person,
        d: person.dedication,
        stranded: person.dedication * loadedCost(person, assumptions),
        plan: strandedPlanOf(design, person),
        closeMonth: closeMonthOf(person, assumptions),
    }));
    /*
      Only received-direction TSAs bring RemainCo fee income, and only for as long as they actually bill.
  
      An agreement recorded as exited went on offsetting stranded cost for its full original term, so the
      exposure was understated for exactly the agreements the user had said were over. `billedMonths`
      answers both cases: none for an exit with no month, and the months up to the exit when there is one.
    */
    const incomeTsas = design.tsas.filter((t) => t.direction !== 'provided' && billedMonths(t) > 0);
    // The stranded timeline's own horizon: received-direction durations and plan months. Not the transition
    // chart's — see the note on `computeTsaEconomics`'s.
    const horizon = horizonOver([
        // The months income actually runs, not the term it was signed for.
        ...incomeTsas.map((t) => billedMonths(t)),
        ...carriers.map((c) => (c.plan ? c.plan.month : 0)),
        // A deferred close steps gross UP at k+1; the axis must reach past the step to draw it.
        ...carriers.map((c) => c.closeMonth),
    ]);
    const monthly = [];
    for (let m = 1; m <= horizon; m++) {
        let gross = 0;
        let income = 0;
        let severance = 0;
        for (const c of carriers) {
            const M = c.plan ? Math.max(1, c.plan.month) : Infinity;
            // Stranded from the month AFTER the local close (m > k; k = 0 reads "from month 1", the
            // Day-1 identity) until the mitigation lands. A plan month at or before the close leaves
            // this carrier accruing nothing — the business never left while they were still here —
            // which is real and diagnosed, not smoothed over: severance still books at the stated month.
            if (m > c.closeMonth && m < M)
                gross += c.stranded / 12;
            if (c.plan && c.plan.action === 'rightsize' && m === M) {
                severance += severanceCostOf(c.person, assumptions);
            }
        }
        for (const t of incomeTsas) {
            // `billedMonths`, so an agreement ended at month 8 of a twelve-month term stops offsetting at 8.
            if (m <= billedMonths(t))
                income += Number(t.monthlyCost) || 0;
        }
        monthly.push({ m, gross, income, net: gross - income, severance });
    }
    const rightsized = carriers.filter((c) => c.plan && c.plan.action === 'rightsize');
    const redeployed = carriers.filter((c) => c.plan && c.plan.action === 'redeploy');
    const residual = carriers.filter((c) => !c.plan);
    // The run-rates below stay whole-population under deferred closes, deliberately: they are the
    // steady-state annual exposure, not a month-1 read, and the bridge identity
    // gross = rightsized share + redeployed + residual must keep closing. The deferred ramp-in is
    // the monthly curve's business alone.
    return {
        carriers,
        horizon,
        monthly,
        grossRunRate: carriers.reduce((a, c) => a + c.stranded, 0),
        tsaIncomeMonthly: monthly.length ? monthly[0].income : 0,
        tsaIncomeMonths: Math.max(0, ...incomeTsas.map((t) => billedMonths(t))),
        rightsizedN: rightsized.length,
        // The FULL loaded cost leaves when someone is rightsized, not just their stranded share.
        rightsizeSavingRunRate: rightsized.reduce((a, c) => a + loadedCost(c.person, assumptions), 0),
        /*
          The stranded share that leaves with them — the number that actually bridges gross to residual, and
          the one the original printed nowhere.
    
          Its five tiles read as a subtraction, minus sign and all, and did not reconcile: gross less the
          full-loaded rightsizing saving less the redeployed share came out below the residual printed beside
          it, by exactly `overRemovalRunRate`. Two run-rates measuring different things were being subtracted
          from one. The monthly series steps by this figure, not by the full loaded saving, which is why a
          reader measuring the chart's step got a saving 2.5× smaller than the tile above it claimed.
    
          This one closes: grossRunRate = rightsizedStrandedShare + redeployedRunRate + residualRunRate.
        */
        rightsizedStrandedShare: rightsized.reduce((a, c) => a + c.stranded, 0),
        severanceOneOff: rightsized.reduce((a, c) => a + severanceCostOf(c.person, assumptions), 0),
        // The (1 − d) share RemainCo still needed: the backfill risk a rightsizing creates.
        overRemovalRunRate: rightsized.reduce((a, c) => a + (1 - c.d) * loadedCost(c.person, assumptions), 0),
        redeployedN: redeployed.length,
        redeployedRunRate: redeployed.reduce((a, c) => a + c.stranded, 0),
        residualN: residual.length,
        residualRunRate: residual.reduce((a, c) => a + c.stranded, 0),
    };
}
/**
 * How much of the dedication question has been answered, and where the people it answers about go.
 *
 * Two halves under one tab, and they measure different cohorts on purpose. The COVERAGE half is about
 * the numeric data — who has a value, how the values are distributed, what fraction of FTE they account
 * for. The ALLOCATION half is about the SHARED cohort (`sharedMarker`: partially dedicated, or carrying
 * the pre-dedication marker) and which side of the separation each of them ends up on.
 *
 * THE WEIGHT IS NOT SCALED BY DEDICATION, which looks like an omission and is the point: the question
 * is where a whole employment contract lands, not how somebody's week divides. A 10%-dedicated and a
 * 90%-dedicated person each count as one whole head going one way.
 *
 * GROUPED ON THE BASE VALUE, like `computeStrandedCapacity`, because this pane has no perspective to
 * resolve through — the perspectives ARE its columns. That is a real limitation and the app shared it,
 * but the app also never said so, printing an as-is group label over a row whose split comes from the
 * current design. Here the caller is told (the group key is documented as the imported value) so it can
 * caption it.
 *
 * NOT A LENS: no filters, no stage, no perspective, no view mode. The frozen pane already read the full
 * roster, which is one of the few places it did.
 */
export function computeDedication(state, options = {}) {
    const design = options.design ?? state.design;
    const metric = options.metric ?? 'hc';
    const levelKey = options.levelKey ?? null;
    const w = metricFn(metric, state.assumptions);
    const population = [...state.employees, ...design.buildUps].filter(countsInAggregates);
    const out = {
        metric,
        anyWeight: false,
        populationN: population.length,
        employeeN: 0,
        buildUpN: 0,
        withDedicationN: 0,
        coverageOfEmployees: 0,
        fullyDedicatedN: 0,
        partiallyDedicatedN: 0,
        zeroDedicatedN: 0,
        legacyOnlyN: 0,
        noDataN: 0,
        dedicatedFte: 0,
        unmeasuredFte: 0,
        bins: Array.from({ length: 10 }, () => 0),
        sharedN: 0,
        sharedWeight: 0,
        toRemain: 0,
        toCarveOut: 0,
        toNoLonger: 0,
        toRemainN: 0,
        toCarveOutN: 0,
        toNoLongerN: 0,
        grandWeight: 0,
        groups: [],
    };
    const groups = new Map();
    const groupOf = (person) => {
        if (!levelKey)
            return null;
        const key = String(person.levels[levelKey] ?? '').trim() || '(none)';
        let g = groups.get(key);
        if (!g) {
            g = {
                key,
                sharedN: 0, sharedWeight: 0, toRemain: 0, toCarveOut: 0, toNoLonger: 0,
                groupWeight: 0, groupN: 0, withDedicationN: 0, noDataN: 0,
            };
            groups.set(key, g);
        }
        return g;
    };
    for (const person of population) {
        const weight = Number(w(person)) || 0;
        const d = person.dedication;
        const g = groupOf(person);
        if (person.isBuildUp)
            out.buildUpN++;
        else
            out.employeeN++;
        out.grandWeight += weight;
        if (g) {
            g.groupWeight += weight;
            g.groupN++;
        }
        if (d != null) {
            out.withDedicationN++;
            if (g)
                g.withDedicationN++;
            if (d === 1)
                out.fullyDedicatedN++;
            else if (d === 0)
                out.zeroDedicatedN++;
            else
                out.partiallyDedicatedN++;
            out.dedicatedFte += (Number(person.fte) || 0) * d;
            // Clamped at nine: a dedication of exactly 1 belongs in the top band, not an eleventh one.
            out.bins[Math.min(9, Math.max(0, Math.floor(d * 10)))]++;
        }
        else {
            if (person.sharedMarker)
                out.legacyOnlyN++;
            else
                out.noDataN++;
            if (g)
                g.noDataN++;
            out.unmeasuredFte += Number(person.fte) || 0;
        }
        if (!person.sharedMarker)
            continue;
        out.sharedN++;
        out.sharedWeight += weight;
        if (g) {
            g.sharedN++;
            g.sharedWeight += weight;
        }
        const bucket = currentBucket(design, person.id);
        if (bucket === 'Carve-out') {
            out.toCarveOut += weight;
            out.toCarveOutN++;
            if (g)
                g.toCarveOut += weight;
        }
        else if (bucket === 'No longer required') {
            out.toNoLonger += weight;
            out.toNoLongerN++;
            if (g)
                g.toNoLonger += weight;
        }
        else {
            out.toRemain += weight;
            out.toRemainN++;
            if (g)
                g.toRemain += weight;
        }
    }
    out.coverageOfEmployees = out.employeeN ? out.withDedicationN / out.employeeN : 0;
    out.anyWeight = out.grandWeight > 0;
    /*
      Emitted on the COUNT, never the weight. The app filtered on weight, so under the Cost metric a
      project with no salary column mapped rendered a table with no body rows at all — while the tile above
      it still counted the shared people the table had just deleted.
    */
    out.groups = [...groups.values()]
        .filter((g) => g.sharedN > 0)
        .sort((a, b) => b.sharedWeight - a.sharedWeight || b.sharedN - a.sharedN || a.key.localeCompare(b.key));
    return out;
}
function freshGroup() {
    return {
        strandedCost: 0, strandedFte: 0, excessCost: 0, excessFte: 0,
        mitigatedCost: 0, mitigatedFte: 0, residualCost: 0, residualFte: 0,
        strandedN: 0, excessN: 0, residualN: 0,
    };
}
/**
 * Two mirrored quantities: stranded capacity left in RemainCo (a Remain-side person's dedicated
 * share, d), and excess capacity taken into the carve-out (a carve member's undedicated share,
 * 1 − d). Grouped by a hierarchy level when one is given.
 */
export function computeStrandedCapacity(state, options = {}) {
    const design = options.design ?? state.design;
    const levelKey = options.levelKey ?? null;
    const { assumptions } = state;
    const groups = new Map();
    const tot = {
        ...freshGroup(),
        strandedN: 0,
        excessN: 0,
        excessExitN: 0,
        noData: 0,
    };
    const bump = (key, fn) => {
        let g = groups.get(key);
        if (!g) {
            g = freshGroup();
            groups.set(key, g);
        }
        fn(g);
    };
    for (const person of state.employees) {
        if (!countsInAggregates(person))
            continue;
        const bucket = currentBucket(design, person.id);
        if (bucket === 'No longer required')
            continue;
        const d = person.dedication;
        const key = levelKey ? String(person.levels[levelKey] ?? '').trim() || '(none)' : '(all)';
        if (d == null) {
            // Unknown dedication on the Remain side is a data gap worth reporting, not a zero.
            if (bucket !== 'Carve-out')
                tot.noData++;
            continue;
        }
        if (bucket === 'Carve-out') {
            if (d < 1) {
                const cost = (1 - d) * loadedCost(person, assumptions);
                const fte = (1 - d) * (Number(person.fte) || 0);
                tot.excessCost += cost;
                tot.excessFte += fte;
                tot.excessN++;
                if ((design.bucketMeta[person.id] ?? {}).day2Exit)
                    tot.excessExitN++;
                bump(key, (g) => {
                    g.excessCost += cost;
                    g.excessFte += fte;
                    g.excessN++;
                });
            }
            continue;
        }
        if (d > 0) {
            const cost = d * loadedCost(person, assumptions);
            const fte = d * (Number(person.fte) || 0);
            tot.strandedCost += cost;
            tot.strandedFte += fte;
            tot.strandedN++;
            const planned = !!strandedPlanOf(design, person);
            if (planned) {
                tot.mitigatedCost += cost;
                tot.mitigatedFte += fte;
            }
            else {
                tot.residualCost += cost;
                tot.residualFte += fte;
                tot.residualN++;
            }
            bump(key, (g) => {
                g.strandedCost += cost;
                g.strandedFte += fte;
                g.strandedN++;
                if (planned) {
                    g.mitigatedCost += cost;
                    g.mitigatedFte += fte;
                }
                else {
                    g.residualCost += cost;
                    g.residualFte += fte;
                    g.residualN++;
                }
            });
        }
    }
    return { groups, tot };
}
/**
 * How much of the TSA income can be placed where the stranded cost is — and how much cannot.
 *
 * Every surface that shows stranded cost nets received-TSA income against it, and the word doing the work
 * was "COVERED". Nothing in the model supports that: `computeStrandedTimeline` sums income across every
 * received agreement regardless of where the capacity sits, so an IT agreement's fee offsets stranded
 * cost in Finance and the caption calls it coverage.
 *
 * The comparison the word implies is computable without changing the schema, because the register's own
 * `area` datalist is built from the FIRST grouping level (`TsaPanel`) — so an area typed through the
 * control IS a level value, and matching them is reading the vocabulary the app already offers rather
 * than inventing one. What it is NOT is enforced: `area` is free text and a file can carry anything, which
 * is exactly why the unmatched and unattributed remainders are returned rather than folded away. A
 * consultant who sees "€400,000 of €900,000 sits where the cost does" learns something true; one who sees
 * only "covered" learns something false.
 *
 * ANNUAL throughout, on both sides. `tsaIncomeMonthly` is a monthly figure and `strandedCost` is annual
 * loaded, and putting the two in one table without converting is the unit defect this milestone has
 * already fixed twice.
 */
export function strandedIncomeAttribution(state, options = {}) {
    const design = options.design ?? state.design;
    const levelKey = state.groupingLevels[0]?.key ?? null;
    // The same population and the same filter the netting uses, so the parts sum to the whole it explains.
    const billing = design.tsas.filter((t) => t.direction !== 'provided' && billedMonths(t) > 0);
    const capacity = computeStrandedCapacity(state, { levelKey, design });
    const strandedByValue = new Map();
    for (const [value, group] of capacity.groups) {
        if (group.strandedCost > 0)
            strandedByValue.set(norm(value), group.strandedCost);
    }
    const incomeByValue = new Map();
    let unmatchedIncome = 0;
    let unattributedIncome = 0;
    let matchedAgreementsN = 0;
    for (const tsa of billing) {
        const annual = (Number(tsa.monthlyCost) || 0) * 12;
        const area = String(tsa.area ?? '').trim();
        if (!area) {
            unattributedIncome += annual;
            continue;
        }
        if (!strandedByValue.has(norm(area))) {
            unmatchedIncome += annual;
            continue;
        }
        matchedAgreementsN++;
        const entry = incomeByValue.get(norm(area)) ?? { label: area, income: 0 };
        entry.income += annual;
        incomeByValue.set(norm(area), entry);
    }
    const rows = [...incomeByValue]
        .map(([key, { label, income }]) => ({
        value: label,
        income,
        strandedCost: strandedByValue.get(key) ?? 0,
    }))
        .sort((a, b) => b.income - a.income || a.value.localeCompare(b.value));
    const named = new Set(rows.map((r) => norm(r.value)));
    let strandedWithoutIncome = 0;
    for (const [key, cost] of strandedByValue)
        if (!named.has(key))
            strandedWithoutIncome += cost;
    return {
        levelKey,
        rows,
        matchedIncome: rows.reduce((a, r) => a + r.income, 0),
        unmatchedIncome,
        unattributedIncome,
        strandedWithoutIncome,
        agreementsN: billing.length,
        matchedAgreementsN,
    };
}
/** Case- and space-insensitive, because an area is typed and a level value is imported. */
function norm(value) {
    return value.trim().toLowerCase();
}
/* ------------------------------------------------------------------ *
 * Scenario KPIs
 * ------------------------------------------------------------------ */
/*
  `scenarioSeparationKpis` used to live here and is deleted rather than carried.

  Its stated purpose was that "the Compare-scenarios table can never disagree with the panes". It had no
  caller anywhere, it was absent from the parity projection that enumerates every other engine in this
  file explicitly, and it could not fill five of that table's thirteen rows — the table wants three metric
  bridges and a raw-salary cost bridge, and this returned four scalars. Worse, it offered `tsaFeesNet`,
  the cumulative signed fee over each agreement's whole life, under the pane's per-month heading "Net TSA
  fees at Day 1 (/mo)" — wrong by roughly `durationMonths`, and it looked right doing it. It also paid for
  a whole monthly loop to keep that one number.

  A function whose only job is to stop two surfaces disagreeing, which nothing ran and the harness never
  compared, is not a head start on the pane. The Compare-scenarios tab is blocked on transport anyway:
  `/design` carries scenario names but no sibling scenario designs, so N scenarios means N full roster
  downloads for a table of thirteen numbers. When that endpoint lands, this gets rebuilt to the pane's
  actual shape — the three bridges, `netMonthAtDay1` rather than `feesNet`, and a TSA count — and added
  to the parity projection, which is the one place a wrong unit does not survive.
*/
//# sourceMappingURL=economics.js.map